import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { lastValueFrom, map, Observable, take } from 'rxjs';
import {
  Attendance,
  AttendanceStats,
} from '../models/attendance.school.model';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SchoolAttendanceService {
  http = inject(HttpClient);
  afs: AngularFirestore = inject(AngularFirestore);
  utilsService = inject(UtilsService);

  constructor() {}

  getAttendanceByStudentId(
    groupId: string,
    studentId: string,
  ): Observable<Attendance> {
    const doc = this.afs
      .collection('groups')
      .doc(groupId)
      .collection<Attendance>('attendances')
      .doc(studentId)
      .get();
    return doc.pipe(
      take(1),
      map((doc) => {
        return { ...doc.data(), id: doc.id } as Attendance;
      }),
    );
  }

  async getAttendanceByGradeId({
    groupId,
    academicYear,
    gradeId,
  }: {
    groupId: string;
    academicYear: number;
    gradeId: string;
  }): Promise<AttendanceStats[] | null> {
    // attendance_stats docs ({studentId}_{year}) carry studentId and
    // academicYear but no gradeId; callers map them onto the grade's
    // students, so scoping to the academic year is what matters here.
    const docRef = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('attendance_stats', (ref) =>
        ref.where('academicYear', '==', academicYear),
      );
    const snapshot = await lastValueFrom(docRef.get());
    if (snapshot.empty) {
      return null;
    }
    return snapshot.docs.map((doc) => {
      return { ...doc.data() } as AttendanceStats;
    });
  }

  async getAttendanceBySessionId(
    groupId: string,
    sessionId: string,
  ): Promise<Attendance[]> {
    const snapshot = await lastValueFrom(
      this.afs
        .collection('groups')
        .doc(groupId)
        .collection<Attendance>('attendances', (ref) =>
          ref.where('sessionId', '==', sessionId),
        )
        .get(),
    );
    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
      } as Attendance;
    });
  }

  async getStudentAttendance({
    groupId,
    academicYear,
    studentId,
  }: {
    groupId: string;
    academicYear: number;
    studentId: string;
  }): Promise<AttendanceStats | undefined> {
    const docRef = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('attendance_stats')
      .doc(`${studentId}_${academicYear}`);
    const snapshot = await lastValueFrom(docRef.get());
    if (!snapshot.exists) {
      return undefined;
    }
    // console.log(
    //   `AttendanceService:: getStudentAttendance: ${studentId} - ${academicYear} - ${JSON.stringify(
    //     snapshot.data()
    //   )}`
    // );
    return snapshot.data() as AttendanceStats;
  }

  async create(groupId: string, attendance: Attendance): Promise<string> {
    // console.log('AttendanceService:: create:', JSON.stringify(attendance, null, 2));
    const doc = await this.afs
      .collection('groups')
      .doc(groupId)
      .collection('attendances')
      .add(attendance);
    return doc.id;
  }

  async update(
    groupId: string,
    lessonId: string,
    attendance: Attendance,
  ): Promise<void> {
    // console.log(
    //   `AttendanceService:: update: GroupId: ${groupId}, LessonId: ${lessonId}, attendance: ${JSON.stringify(
    //     attendance,
    //     null,
    //     2
    //   )}`
    // );
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection<Attendance>('attendances')
      .doc(lessonId)
      .set(attendance, { merge: true });
  }

  async delete(groupId: string, id: string): Promise<void> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection<Attendance>('attendances')
      .doc(id)
      .delete();
  }

  async getAttendanceStats(groupId: string): Promise<any> {
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'attendanceCount');
      const result = await callable({
        groupId,
      });
      // console.log(`Response: ${JSON.stringify(result)}`);
      return result.data;
    } catch (error) {
      // console.log(`AttendanceService :: getAttendanceStats: Error: ${error}`);
      return Promise.reject('Unable to fetch Attendance Statistics');
    }
  }
}
